/** helpers */
export const isInt = v => /^\d+$/.test(String(v ?? ''));
export const isPlainObject = v => v && typeof v === 'object' && !Array.isArray(v);

/**
 * Use for consistent subField fieldName definition in dynamic and fixed arrays
 */
export const arrayPath = (parentCode, arrayIndex, childCode) =>
  `${parentCode}.${arrayIndex}.${childCode}`;

/**
 * Path splitter for array fields.
 *
 * Accepts only:
 *  - actual paths:   "parent.child", "parent.3.child"
 *  - watched patterns: "parent.child", "parent.{i}.child"
 *
 * Returns:
 *  {
 *    parentCode: string|null,
 *    arrayIndex: number|null,        // only for actual paths with a numeric index
 *    childCode: string|null,         // first child segment after parent/index
 *    hasIndexPlaceholder: boolean,   // true when path uses "{i}" in index position
 *    base: string                    // "parent.child" (no index), handy for compares
 *  }
 */
export const splitArrayPath = (fieldPath = '') => {
  const s = String(fieldPath || '').trim();
  if (!s) {
    return {
      parentCode: null,
      arrayIndex: null,
      childCode: null,
      hasIndexPlaceholder: false,
      pathNoIndex: '',
    };
  }

  // Split on dots only
  const parts = s.split('.').filter(Boolean);

  const parentCode = parts[0] ?? null;
  const second = parts[1] ?? null;

  const hasIndexPlaceholder = second === '{i}';
  const arrayIndex = !hasIndexPlaceholder && isInt(second) ? Number(second) : null;

  // childCode is the segment immediately after the index (if present),
  // otherwise the segment immediately after the parent
  const childCode = hasIndexPlaceholder
    ? (parts[2] ?? null)
    : isInt(second)
      ? (parts[2] ?? null)
      : (parts[1] ?? null);

  const pathNoIndex = [parentCode, childCode].filter(Boolean).join('.');

  return { parentCode, arrayIndex, childCode, hasIndexPlaceholder, pathNoIndex };
};

/**
 * Walk a values snapshot and yield { path, value } and
 * produces RHF-style dotted paths, including numeric array indexes.
 * Example:
 *  { a: { b: 1 }, list: [{ x: 2 }, { x: 3 }] }
 *  -> "a.b"=1, "list.0.x"=2, "list.1.x"=3
 */
export const convertObjectToDotPaths = (root, base = '', out = {}) => {
  if (Array.isArray(root)) {
    root.forEach((item, i) => {
      const p = base ? `${base}.${i}` : String(i);
      if (Array.isArray(item) || isPlainObject(item)) {
        convertObjectToDotPaths(item, p, out);
      } else {
        out[p] = item;
      }
    });
    return out;
  }
  if (isPlainObject(root)) {
    Object.entries(root).forEach(([k, v]) => {
      const p = base ? `${base}.${k}` : k;
      if (Array.isArray(v) || isPlainObject(v)) {
        convertObjectToDotPaths(v, p, out);
      } else {
        out[p] = v;
      }
    });
    return out;
  }
  // primitive root
  if (base) out[base] = root;
  return out;
};

/**
 * Reads/extracts a value from a nested object using dot notation name path.
 */
export const readValueByName = (root, name) => {
  if (!root || !name) return undefined;
  const parts = name.split('.').filter(Boolean);
  let cur = root;
  for (const seg of parts) {
    if (cur == null) return undefined;
    cur = cur[seg];
  }
  return cur;
};

/**
 * Immutably updates a nested object/array by setting a value at a specific dot-notation path,
 * creating the necessary structure if it doesn't exist.
 */
export const upsertValueByName = (root, name, value) => {
  if (!name) return root;

  const parts = name.split('.').filter(Boolean);
  if (parts.length === 0) return root;

  const out = Array.isArray(root) ? [...(root ?? [])] : { ...(root ?? {}) };
  let cur = out;

  for (let i = 0; i < parts.length - 1; i++) {
    const seg = parts[i];
    const nextSeg = parts[i + 1];
    const existing = cur[seg];

    let container;
    if (Array.isArray(existing)) {
      container = [...existing];
    } else if (existing && typeof existing === 'object') {
      container = { ...existing };
    } else {
      container = isInt(nextSeg) ? [] : {};
    }

    cur[seg] = container;
    cur = container;
  }

  const last = parts[parts.length - 1];
  cur[last] = value;
  return out;
};

/**
 * Finds the fieldCode in formValues
 */
export const resolveFieldValue = (fieldCode, formValues, context = {}) => {
  if (!fieldCode || !formValues) return undefined;

  // Quick direct hit
  if (formValues[fieldCode] !== undefined) return formValues[fieldCode];

  // Try to parse the fieldCode itself
  const { parentCode, arrayIndex, childCode } = splitArrayPath(fieldCode);

  // Case A: "parent.index.child" explicitly in code
  if (parentCode && Number.isInteger(arrayIndex) && childCode) {
    return formValues?.[parentCode]?.[arrayIndex]?.[childCode];
  }

  // Case B: "parent.child" (no index in code) — may use context to inject index
  if (parentCode && !Number.isInteger(arrayIndex) && childCode) {
    // Prefer explicit context if it points to the same parent
    if (context.parentField?.code === parentCode && Number.isInteger(context.arrayIndex)) {
      return formValues?.[parentCode]?.[context.arrayIndex]?.[childCode];
    }

    // Check if the parentCode is an object and has the childCode property
    if (
      formValues?.[parentCode] &&
      typeof formValues[parentCode] === 'object' &&
      !Array.isArray(formValues[parentCode])
    ) {
      const v = formValues[parentCode][childCode];
      if (v !== undefined) return v;
    }

    // Check if childCode is top-level in formValues
    if (formValues[childCode] !== undefined) return formValues[childCode];
  }

  // Case C: flat key like "country" or unmapped shapes
  return formValues[fieldCode]; // may be undefined
};
